import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../lib/api';
import { formatDate } from '../lib/format';
import { MAX_TICKET_IMAGES, ticketImageUrl, uploadTicketImage, type TicketImage } from '../lib/ticket-images';
import type { WorkOrderDetail } from '../lib/types';
import { useToast } from './toast';
import { Card, CardHeader, cn, ConfirmDialog, Field, Spinner } from './ui';

const imageFiles = (files: Iterable<File>) => [...files].filter((file) => file.type.startsWith('image/'));

/**
 * Pictures pasted anywhere on the page, such as a screenshot, while `accept` agrees. Pastes that carry text are left
 * alone, so pasting into a text box still works.
 */
function usePastedPictures(enabled: boolean, onFiles: (files: File[]) => void, accept: (target: EventTarget | null) => boolean) {
  const latest = useRef({ onFiles, accept });
  useEffect(() => {
    latest.current = { onFiles, accept };
  });
  useEffect(() => {
    if (!enabled) return;
    const onPaste = (event: ClipboardEvent) => {
      const data = event.clipboardData;
      if (!data || data.types.includes('text/plain') || !latest.current.accept(event.target)) return;
      const files = imageFiles(data.files);
      if (files.length === 0) return;
      event.preventDefault();
      latest.current.onFiles(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [enabled]);
}

/** Drag-and-drop of picture files onto an area. */
function useDropPictures(onFiles: (files: File[]) => void) {
  const [over, setOver] = useState(false);
  const hasFiles = (event: DragEvent) => event.dataTransfer.types.includes('Files');
  return {
    over,
    props: {
      onDragOver: (event: DragEvent) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setOver(true);
      },
      onDragLeave: (event: DragEvent) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(false);
      },
      onDrop: (event: DragEvent) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setOver(false);
        const files = imageFiles(event.dataTransfer.files);
        if (files.length > 0) onFiles(files);
      },
    },
  };
}

function ChooseButton({ children, disabled, onFiles, className }: { children: ReactNode; disabled?: boolean; onFiles(files: File[]): void; className?: string }) {
  return (
    <label className={cn('cursor-pointer focus-within:ring-2 focus-within:ring-brand-500', disabled && 'pointer-events-none opacity-60', className)}>
      {children}
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp,image/*"
        multiple
        disabled={disabled}
        className="sr-only"
        onChange={(event) => {
          const files = imageFiles(event.target.files ?? []);
          event.target.value = '';
          if (files.length > 0) onFiles(files);
        }}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// On the new ticket form
// ---------------------------------------------------------------------------

export interface PendingPicture {
  key: string;
  name: string;
  preview: string;
  /** Set once it's uploaded. */
  id: string | null;
  error: string | null;
}

/** Pictures chosen on a form, each uploaded as soon as it's picked, for the form to attach when it's sent. */
export function usePendingPictures() {
  const [pictures, setPictures] = useState<PendingPicture[]>([]);
  const current = useRef(pictures);
  useEffect(() => {
    current.current = pictures;
  });
  useEffect(() => () => current.current.forEach((picture) => URL.revokeObjectURL(picture.preview)), []);

  const add = useCallback((files: File[]) => {
    const room = Math.max(0, MAX_TICKET_IMAGES - current.current.length);
    const added = files.slice(0, room).map((file) => ({
      file,
      picture: { key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`, name: file.name || 'Pasted picture', preview: URL.createObjectURL(file), id: null, error: null },
    }));
    if (added.length === 0) return;
    // Counted right away, so pictures added again before the next render still stop at the limit.
    current.current = [...current.current, ...added.map((entry) => entry.picture)];
    setPictures((list) => [...list, ...added.map((entry) => entry.picture)]);
    for (const { file, picture } of added) {
      const settle = (patch: Partial<PendingPicture>) => setPictures((list) => list.map((item) => (item.key === picture.key ? { ...item, ...patch } : item)));
      uploadTicketImage(file).then(
        (saved) => settle({ id: saved.id }),
        (err) => settle({ error: errorMessage(err) }),
      );
    }
  }, []);

  const remove = useCallback((key: string) => {
    setPictures((list) =>
      list.filter((picture) => {
        if (picture.key === key) URL.revokeObjectURL(picture.preview);
        return picture.key !== key;
      }),
    );
  }, []);

  const reset = useCallback(() => {
    setPictures((list) => {
      list.forEach((picture) => URL.revokeObjectURL(picture.preview));
      return [];
    });
  }, []);

  return {
    pictures,
    add,
    remove,
    reset,
    uploading: pictures.some((picture) => !picture.id && !picture.error),
    ids: pictures.flatMap((picture) => (picture.id ? [picture.id] : [])),
  };
}

/** The "Screenshots or photos" field: thumbnails of what's chosen, and a tile to choose or drop more. Pasting works too. */
export function PicturesField({ pending, active }: { pending: ReturnType<typeof usePendingPictures>; /** Listen for pasted pictures. */ active: boolean }) {
  const area = useRef<HTMLDivElement>(null);
  const drop = useDropPictures(pending.add);
  // Pastes inside the dialog this field is in.
  usePastedPictures(active, pending.add, (target) => {
    const dialog = area.current?.closest('[role=dialog]');
    return !dialog || (target instanceof Node && dialog.contains(target));
  });
  const full = pending.pictures.length >= MAX_TICKET_IMAGES;

  return (
    <Field as="div" label="Screenshots or photos" hint={`Optional - up to ${MAX_TICKET_IMAGES}. Paste a screenshot, drop pictures here, or choose them.`}>
      <div ref={area} {...drop.props} className={cn('grid grid-cols-3 gap-2 rounded-lg sm:grid-cols-5', drop.over && 'ring-2 ring-brand-400 ring-offset-2')}>
        {pending.pictures.map((picture) => (
          <div key={picture.key} className="relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
            <img src={picture.preview} alt={picture.name} className="size-full object-cover" />
            {!picture.id && !picture.error && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                <Spinner />
              </div>
            )}
            {picture.error && (
              <div role="alert" className="absolute inset-0 flex items-center justify-center overflow-hidden bg-red-50/95 p-1.5 text-center text-[11px] leading-tight text-red-700">
                {picture.error}
              </div>
            )}
            <button
              type="button"
              onClick={() => pending.remove(picture.key)}
              className="absolute top-1 right-1 rounded-full bg-slate-900/70 p-0.5 text-white hover:bg-slate-900"
              aria-label={`Remove ${picture.name}`}
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
        {!full && (
          <ChooseButton
            onFiles={pending.add}
            className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 p-1 text-center text-xs text-slate-500 hover:border-brand-400 hover:text-brand-700"
          >
            <ImagePlus className="size-5" />
            Add pictures
          </ChooseButton>
        )}
      </div>
    </Field>
  );
}

// ---------------------------------------------------------------------------
// On the ticket
// ---------------------------------------------------------------------------

/** A ticket's screenshots and photos. The requester and technicians add more by choosing, dropping, or pasting them. */
export function TicketPicturesCard({ ticket }: { ticket: WorkOrderDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(0);
  const [removing, setRemoving] = useState<TicketImage | null>(null);
  const canAdd = ticket.permissions.addImages;
  const room = MAX_TICKET_IMAGES - ticket.images.length;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['work-order', ticket.id] });

  const add = async (files: File[]) => {
    if (!canAdd || uploading > 0) return;
    const chosen = files.slice(0, room);
    if (files.length > room) toast.error(`A ticket can have up to ${MAX_TICKET_IMAGES} pictures`);
    setUploading(chosen.length);
    let added = 0;
    for (const file of chosen) {
      try {
        await uploadTicketImage(file, ticket.id);
        added += 1;
      } catch (err) {
        toast.error(errorMessage(err));
      }
      setUploading((count) => count - 1);
    }
    await refresh();
    if (added > 0) toast.success(added === 1 ? 'Picture added' : `${added} pictures added`);
  };

  const drop = useDropPictures((files) => void add(files));
  // Pastes anywhere on the ticket, except in a dialog opened over it.
  usePastedPictures(canAdd, (files) => void add(files), (target) => !(target instanceof Element && target.closest('[role=dialog]')));

  const remove = useMutation({
    mutationFn: (image: TicketImage) => api.delete(`/work-orders/${ticket.id}/images/${image.id}`),
    onSuccess: async () => {
      await refresh();
      setRemoving(null);
      toast.success('Picture removed');
    },
    onError: (err) => {
      setRemoving(null);
      toast.error(errorMessage(err));
    },
  });

  return (
    <Card>
      <div {...drop.props} className={cn('rounded-xl', drop.over && canAdd && 'ring-2 ring-brand-400')}>
        <CardHeader
          title="Screenshots & photos"
          description={canAdd ? 'Paste a screenshot, or drop pictures here' : undefined}
          actions={
            canAdd && (
              <ChooseButton
                onFiles={(files) => void add(files)}
                disabled={uploading > 0}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {uploading > 0 ? <Spinner className="size-4" /> : <ImagePlus className="size-4" />}
                {uploading > 0 ? 'Adding…' : 'Add pictures'}
              </ChooseButton>
            )
          }
        />
        {ticket.images.length === 0 ? (
          <p className="px-5 py-4 text-sm text-slate-500">No pictures yet.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-4">
            {ticket.images.map((image) => (
              <figure key={image.id} className="relative min-w-0">
                <a
                  href={ticketImageUrl(ticket.id, image.id)}
                  target="_blank"
                  rel="noreferrer"
                  title={`Open ${image.fileName}`}
                  className="block aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100 hover:border-brand-300"
                >
                  <img src={ticketImageUrl(ticket.id, image.id)} alt={image.fileName} loading="lazy" className="size-full object-cover" />
                </a>
                <figcaption className="mt-1 truncate text-xs text-slate-500">
                  {image.uploadedByName} · {formatDate(image.createdAt)}
                </figcaption>
                {image.canDelete && (
                  <button
                    type="button"
                    onClick={() => setRemoving(image)}
                    className="absolute top-1.5 right-1.5 rounded-full bg-slate-900/70 p-1 text-white hover:bg-slate-900"
                    aria-label={`Remove ${image.fileName}`}
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </figure>
            ))}
          </div>
        )}
      </div>
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
        loading={remove.isPending}
        title="Remove this picture?"
        description={removing ? `${removing.fileName} comes off the ticket for everyone.` : ''}
        confirmLabel="Remove"
      />
    </Card>
  );
}
